import classNames from "clsx";
import React from "react";
import { ProgressBar as AriaProgressbar } from "react-aria-components";
import { icon } from "../../icon.css.ts";
import { AlertCircle } from "../../icons/line.tsx";
import { base, fadeOutAnimationStyle, spinner, tick } from "../Spinner.css.ts";
import type { Color, SpinnerAppearance } from "../SpinnerAppearance.ts";
import useI18n from "./useI18n.ts";
import type { LoadingState } from "./useSuccessSpinnerState.ts";

export interface Props extends SpinnerAppearance {
  renderState: LoadingState | undefined;
  startAnimation: boolean;
  onSuccessAnimationEnd: () => void;
}

const PlainSpinner: React.FC<{
  color: Color;
  startAnimation: boolean;
}> = ({ color, startAnimation }) => (
  <svg fill="none" viewBox="0 0 24 24" className={spinner({ color })}>
    <path
      className={classNames(startAnimation && fadeOutAnimationStyle)}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.57538 4.57538C5.74706 3.4037 7.17941 2.52595 8.75532 2.01391c1.57588-.51205 3.25068-.64385 4.88728-.38464 1.6366.25922 3.1886.90209 4.5291 1.87605 1.3406.97397 2.4316 2.25138 3.1839 3.72778C22.1078 8.70951 22.5 10.343 22.5 12c0 1.657-.3922 3.2905-1.1444 4.7669-.7523 1.4764-1.8433 2.7538-3.1839 3.7278-1.3405.9739-2.8925 1.6168-4.5291 1.876"
    />
  </svg>
);

const SuccessCheck: React.FC<{
  startAnimation: boolean;
  onAnimationEnd: () => void;
}> = ({ startAnimation, onAnimationEnd }) => (
  <svg
    fill="none"
    viewBox="0 0 16 16"
    className={tick({ startAnimation })}
    // animationend bubbles, so check the target: only this element's own
    // animation ends the success state, never one added to the check inside.
    onAnimationEnd={(event) => {
      if (event.target === event.currentTarget) {
        onAnimationEnd();
      }
    }}
  >
    <path
      d="M13.3332 4L5.99984 11.3333L2.6665 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** A spinner whose state the caller owns. `Spinner` runs this off its own `state`. */
export const ControlledSpinner: React.FC<Props> = ({
  size = "m",
  color = "royalBlue",
  renderState,
  startAnimation,
  onSuccessAnimationEnd,
  ref,
  ...props
}) => {
  const i18n = useI18n();
  const ariaLabel =
    props["aria-labelledby"] == null && props["aria-label"] == null
      ? i18n.format("loading")
      : props["aria-label"];

  if (renderState === "error") {
    return (
      <AriaProgressbar
        aria-label={ariaLabel}
        className={base({ size, error: true })}
        isIndeterminate
        ref={ref}
        {...props}
      >
        <AlertCircle
          className={classNames(icon({ size }), {
            [fadeOutAnimationStyle]: startAnimation,
          })}
        />
        <SuccessCheck
          startAnimation={startAnimation}
          onAnimationEnd={onSuccessAnimationEnd}
        />
      </AriaProgressbar>
    );
  }

  if (renderState === "success") {
    return null;
  }

  return (
    <AriaProgressbar
      aria-label={ariaLabel}
      className={base({ size })}
      isIndeterminate
      ref={ref}
      {...props}
    >
      <PlainSpinner startAnimation={startAnimation} color={color} />
      <SuccessCheck
        startAnimation={startAnimation}
        onAnimationEnd={onSuccessAnimationEnd}
      />
    </AriaProgressbar>
  );
};
